import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { QueryParams } from "@router/route-params";
import { getBooleanQueryParam } from "@utils/url-utils";

/** State for dev feature toggles */
export type DevFeaturesState = {
  /** Whether to show all the plans or only the main ones */
  shouldShowAllPlans: boolean;

  /** Whether to show all the products or only the main ones */
  shouldShowAllProducts: boolean;

  /** Whether to show full time format including hour, minute and second */
  shouldShowFullDateTime: boolean;

  /** Whether to show hardware licenses such as table and plan creation option or not */
  shouldShowHardwareLicense: boolean;

  /** Whether to allow sending Sentry errors from localhost and numbered environments */
  isSentryEnabled: boolean;

  /** Whether to allow selecting several rows of the plans table to act on them at once */
  shouldAllowBulkOperations: boolean;

  /** The colour scheme the user chose, where "system" follows the one of the operating system */
  themeMode: ThemeModePreference;
};

/** The colour scheme choices offered to the user */
export type ThemeModePreference = "light" | "dark" | "system";

/** Used when nothing valid was kept from earlier */
const DEFAULT_THEME_MODE: ThemeModePreference = "system";

/** Where the choice of the colour scheme is kept, so that it survives a reload */
const THEME_MODE_STORAGE_KEY = "admin-panel-theme-mode";

function isThemeModePreference(value: unknown): value is ThemeModePreference {
  return value === "light" || value === "dark" || value === "system";
}

/** @returns the colour scheme choice kept from earlier, "system" when nothing valid can be read */
function readStoredThemeMode(): ThemeModePreference {
  try {
    const stored = globalThis.localStorage.getItem(THEME_MODE_STORAGE_KEY);
    return isThemeModePreference(stored) ? stored : DEFAULT_THEME_MODE;
  } catch {
    return DEFAULT_THEME_MODE;
  }
}

/** Keeps the choice of the colour scheme, where the browser lets it be kept */
export function storeThemeMode(themeMode: ThemeModePreference): void {
  try {
    globalThis.localStorage.setItem(THEME_MODE_STORAGE_KEY, themeMode);
  } catch {
    // The choice then only lasts until the page is reloaded
  }
}

/** Initial state for dev features slice */
const initialDevFeaturesState: DevFeaturesState = {
  /** By default, only the main plans are shown */
  shouldShowAllPlans: false,

  /** By default, only the main products are shown */
  shouldShowAllProducts: false,

  /** By default, only the date is shown */
  shouldShowFullDateTime: false,

  /** By default, hardware licenses are not shown */
  shouldShowHardwareLicense: false,

  /** By default, Sentry errors are not sent from localhost and numbered environments */
  isSentryEnabled: getBooleanQueryParam(
    globalThis.location.search,
    QueryParams.enableSentry
  ),

  /** By default, the plans are only acted on one by one */
  shouldAllowBulkOperations: false,

  /** By default, the choice kept from earlier, otherwise the system's */
  themeMode: readStoredThemeMode(),
};

/** Slice to manage dev features state */
const devFeaturesSlice = createSlice({
  name: "devFeatures",
  initialState: initialDevFeaturesState,
  reducers: {
    /** Sets whether to show all the plans or only the main ones */
    setShouldShowAllPlans(state, action: PayloadAction<boolean>) {
      state.shouldShowAllPlans = action.payload;
    },

    /** Sets whether to show all the products or only the main ones */
    setShouldShowAllProducts(state, action: PayloadAction<boolean>) {
      state.shouldShowAllProducts = action.payload;
    },

    /** Sets whether to show full date and time or not */
    setShouldShowFullDateTime(state, action: PayloadAction<boolean>) {
      state.shouldShowFullDateTime = action.payload;
    },

    /** Sets whether to show hardware licenses or not */
    setShouldShowHardwareLicense(state, action: PayloadAction<boolean>) {
      state.shouldShowHardwareLicense = action.payload;
    },

    /** Sets whether to allow Sentry errors from numbered environments or not */
    setIsSentryEnabled(state, action: PayloadAction<boolean>) {
      state.isSentryEnabled = action.payload;
    },

    /** Sets whether to allow bulk operations on the plans table or not */
    setShouldAllowBulkOperations(state, action: PayloadAction<boolean>) {
      state.shouldAllowBulkOperations = action.payload;
    },

    /** Sets the colour scheme the user chose */
    setThemeMode(state, action: PayloadAction<ThemeModePreference>) {
      state.themeMode = action.payload;
    },

    /** Resets the dev features state to its initial values */
    resetDevFeaturesState: () => initialDevFeaturesState,
  },
  selectors: {
    /** Selector to get the entire dev features state */
    devFeaturesStateSelector: (state) => state,
  },
});

export const {
  setShouldShowAllPlans,
  setShouldShowAllProducts,
  setShouldShowFullDateTime,
  setShouldShowHardwareLicense,
  setIsSentryEnabled,
  setShouldAllowBulkOperations,
  setThemeMode,
  resetDevFeaturesState,
} = devFeaturesSlice.actions;

export const { devFeaturesStateSelector } = devFeaturesSlice.selectors;

export const devFeaturesReducer = devFeaturesSlice.reducer;
